import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Archive as ArchiveIcon, FileText, Trash2 } from 'lucide-react';

const KIND_META = {
  payment_proof: { label: 'Bukti Bayar Invoice', color: 'bg-emerald-100 text-emerald-800' },
  direct_expense: { label: 'Pembelian Langsung', color: 'bg-amber-100 text-amber-800' },
  pr_receipt: { label: 'Tanda Terima PR', color: 'bg-blue-100 text-blue-800' },
};

const isImage = (u) => u && (/\.(jpe?g|png|webp|gif)(\?|$)/i.test(u) || u.startsWith('data:image'));

const absUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http') || u.startsWith('data:')) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const ArchivePage = () => {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [locationId, setLocationId] = useState('all');
  const [kind, setKind] = useState('all');
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [deletingRef, setDeletingRef] = useState(null);

  const canDelete = user?.role === 'super_admin';

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ year: String(year), month: String(month), kind });
    if (locationId && locationId !== 'all') params.set('location_id', locationId);
    api.get(`/archive/receipts?${params}`)
      .then((r) => setData(r.data || { items: [], total: 0 }))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/locations').then((r) => setLocations(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [year, month, locationId, kind]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Hapus bukti "${item.title}" dari arsip? File akan ikut terhapus.`)) return;
    setDeletingRef(item.ref_id);
    try {
      await api.delete('/archive/receipts', { data: { kind: item.kind, ref_id: item.ref_id } });
      toast.success('Bukti dihapus dari arsip');
      setData((prev) => ({
        ...prev,
        items: prev.items.filter((x) => !(x.kind === item.kind && x.ref_id === item.ref_id)),
        total: Math.max(0, (prev.total || 0) - 1),
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal hapus bukti');
    } finally {
      setDeletingRef(null);
    }
  };

  const monthOptions = useMemo(() => (
    ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember']
      .map((label, i) => ({ value: i + 1, label }))
  ), []);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, [now]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="archive-page">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Arsip</p>
          <h1 className="text-4xl font-medium tracking-tight flex items-center gap-3">
            <ArchiveIcon className="w-8 h-8" />
            Arsip Bukti
          </h1>
          <p className="text-muted-foreground mt-1">Kumpulan semua file bukti: pembayaran invoice, pembelian langsung, tanda terima PR</p>
        </div>

        <Card className="border-border">
          <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Bulan</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger data-testid="filter-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {monthOptions.map((m) => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tahun</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger data-testid="filter-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cabang</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger data-testid="filter-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Cabang</SelectItem>
                  {locations.map((l) => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Jenis Bukti</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="filter-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Jenis</SelectItem>
                  <SelectItem value="payment_proof">Bukti Bayar Invoice</SelectItem>
                  <SelectItem value="direct_expense">Pembelian Langsung</SelectItem>
                  <SelectItem value="pr_receipt">Tanda Terima PR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-medium">
              {loading ? 'Memuat...' : `${data.total} Bukti`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {!loading && data.items.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <ArchiveIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Tidak ada bukti pada periode ini</p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.items.map((it, idx) => {
                const meta = KIND_META[it.kind] || { label: it.kind, color: 'bg-gray-100 text-gray-800' };
                const url = absUrl(it.url);
                const img = isImage(it.url);
                const key = `${it.kind}-${it.ref_id || idx}`;
                return (
                  <div
                    key={key}
                    className="group relative border border-border rounded-lg overflow-hidden bg-card hover:shadow-md transition-shadow"
                    data-testid={`archive-item-${idx}`}
                  >
                    {canDelete && it.ref_id && (
                      <button
                        type="button"
                        onClick={() => handleDelete(it)}
                        disabled={deletingRef === it.ref_id}
                        className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/90 shadow border border-border text-destructive hover:bg-destructive hover:text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                        title="Hapus bukti"
                        data-testid={`archive-delete-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <div className="aspect-video bg-secondary/40 flex items-center justify-center overflow-hidden">
                      {img ? (
                        <img src={url} alt={it.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-muted-foreground flex flex-col items-center gap-1">
                          <FileText className="w-10 h-10" />
                          <span className="text-xs">PDF / File</span>
                        </div>
                      )}
                    </div>
                    <div className="p-3 space-y-1">
                      <Badge className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
                      <p className="text-sm font-medium line-clamp-1" title={it.title}>{it.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{it.subtitle}</p>
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-[11px] text-muted-foreground">
                          {it.ticket_id && (
                            <Link to={`/tickets/${it.ticket_id}`} className="hover:underline text-primary">
                              {it.ticket_id}
                            </Link>
                          )}
                          {it.location_id && <span className="ml-2 font-mono">{it.location_id}</span>}
                        </span>
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-primary hover:underline"
                        >
                          Lihat
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ArchivePage;
